import React, { useState } from 'react';
import { Users, Plus, ShieldCheck, UserCheck, Briefcase } from 'lucide-react';
import ParticipantsList from '../participants/ParticipantsList';
import AddParticipantModal from '../participants/AddParticipantModal';
import AssignMandatoryModal from '../participants/AssignMandatoryModal';
import { motion } from 'framer-motion';
import { deleteParticipant } from '../../api/forumAPI';
import { useNotification } from '../../../../context/NotificationContext';

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 10 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.3 } }
};

export default function DossierParticipants({ dossier, participants, onRefresh }) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [participantToEdit, setParticipantToEdit] = useState(null);
  
  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [assignRole, setAssignRole] = useState(null);

  const { showToast } = useNotification();

  const handleOpenAddModal = () => {
    setParticipantToEdit(null);
    setIsModalOpen(true);
  };

  const handleEditParticipant = (participant) => {
    setParticipantToEdit(participant);
    setIsModalOpen(true);
  };

  const handleDeleteParticipant = async (participantId) => {
    try {
      await deleteParticipant(dossier.id, participantId);
      showToast("Intervenant retiré avec succès", "success");
      if (onRefresh) onRefresh();
    } catch (error) {
      console.error("Erreur lors de la suppression du participant", error);
      showToast("Erreur lors de la suppression", "error");
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setTimeout(() => setParticipantToEdit(null), 300);
  };

  const handleAssignClick = (role) => {
    setAssignRole(role);
    setAssignModalOpen(true);
  };

  const MandatoryCard = ({ title, roleKey, icon: Icon, data }) => (
    <div className={`workspace-card p-4 flex flex-col items-center justify-center text-center space-y-3 ${!data ? 'border-dashed border-danger-300 bg-danger-50/50' : 'border-workspace'}`}>
      <div className={`w-12 h-12 rounded-full flex items-center justify-center ${data ? 'bg-brand/10 text-brand' : 'bg-danger-100 text-danger-500'}`}>
        <Icon className="w-6 h-6" />
      </div>
      <div>
        <h4 className="font-bold text-primary">{title}</h4>
        {data ? (
          <p className="text-sm font-medium text-brand mt-1">{data.Nom}</p>
        ) : (
          <p className="text-xs text-danger-600 mt-1 flex items-center justify-center">
            <span className="w-2 h-2 rounded-full bg-danger-500 mr-1.5 animate-pulse"></span>
            Non assigné
          </p>
        )}
      </div>
      <button 
        onClick={() => handleAssignClick(roleKey)}
        className={`btn btn-sm mt-2 ${data ? 'btn-secondary text-xs' : 'btn-primary text-xs bg-danger-600 hover:bg-danger-700 border-transparent text-white'}`}
      >
        {data ? 'Modifier' : 'Assigner'}
      </button>
    </div>
  );

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="space-y-6"
    >
      <motion.div variants={itemVariants} className="flex flex-col sm:flex-row sm:justify-between sm:items-center workspace-card p-6 gap-4">
        <div>
          <h2 className="text-lg font-bold text-primary flex items-center">
            <ShieldCheck className="w-5 h-5 mr-2 text-tertiary" />
            Acteurs clés (comité de décision)
          </h2>
          <p className="text-sm text-tertiary mt-1">
            Chaque acteur dispose de droits opposables/non-opposable sur le dossier. Le décideur décide de valider ou non un dossier (affectation sur le statut du dossier).
          </p>
        </div>
      </motion.div>

      <motion.div variants={itemVariants} className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <MandatoryCard title="Référent Client" roleKey="referent" icon={UserCheck} data={dossier?.referent} />
        <MandatoryCard title="Décideur" roleKey="decideur" icon={ShieldCheck} data={dossier?.decideur} />
        <MandatoryCard title="Avocat" roleKey="avocat" icon={Briefcase} data={dossier?.avocat} />
      </motion.div>

      <motion.div variants={itemVariants} className="flex flex-col sm:flex-row sm:justify-between sm:items-center workspace-card p-6 gap-4 mt-8">
        <div>
          <h2 className="text-lg font-bold text-primary flex items-center">
            <Users className="w-5 h-5 mr-2 text-tertiary" />
            Autres Contacts Externes
          </h2>
          <p className="text-sm text-tertiary mt-1">Gérez les autres partenaires et intervenants associés à l'opération.</p>
        </div>
        <button
          onClick={handleOpenAddModal}
          className="btn btn-primary flex items-center w-full sm:w-auto justify-center"
        >
          <Plus className="w-4 h-4 mr-2" />
          Ajouter un contact
        </button>
      </motion.div>

      <motion.div variants={itemVariants} className="workspace-card p-6">
        <ParticipantsList
          participants={participants}
          onEdit={handleEditParticipant}
          onDelete={handleDeleteParticipant}
        />
      </motion.div>

      <AddParticipantModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        dossierId={dossier?.id}
        participantToEdit={participantToEdit}
        onSuccess={() => {
          if (onRefresh) onRefresh();
        }}
      />

      <AssignMandatoryModal
        isOpen={assignModalOpen}
        onClose={() => setAssignModalOpen(false)}
        dossierId={dossier?.id}
        roleType={assignRole}
        currentValue={assignRole ? dossier?.[assignRole] : null}
        onSuccess={() => {
          if (onRefresh) onRefresh();
        }}
      />
    </motion.div>
  );
}
